import { model as i18n } from '!/i18n'
import { model as cartModel } from '$/cart'
import { model as paymentInfoModel } from '$/payment-info'
import { model as priceSettingsModel } from '$/price-settings'
import {
  model as redirectUrlModel,
  type TvChannelUrlLibraryParams,
  type TvChannelUrlSpecialParams,
} from '$/redirect-url'
import { api } from '@setplex/tria-api'
import { createEvent, sample } from 'effector'
import type { TvChannelPayment } from '../../interfaces'
import {
  getPerItemPriceSettingsOnly,
  getPerSetPriceSettingsOnly,
  isPaid,
  isPayForBundle,
  isPayForItem,
} from '../../lib'
import { formatChannelExternalPayment } from '../../lib/channels'
import * as external from './external'
import { anyPaymentInitiated } from './model'

export const selectTvChannel = createEvent<
  TvChannelPayment & TvChannelUrlLibraryParams & TvChannelUrlSpecialParams
>()

export const selectTvChannelOnEpg = createEvent<TvChannelPayment>()

const selectForInternalPayment = createEvent<TvChannelPayment>()

export const payForTvChannel = createEvent<TvChannelPayment>()

export const payForTvBundle = createEvent<TvChannelPayment>()

sample({
  clock: selectTvChannel,
  fn: (selectTvChannelParams) => ({
    ...selectTvChannelParams,
    categoryId: selectTvChannelParams.categoryId ?? 0,
  }),
  target: redirectUrlModel.saveTvChannelRedirectUrl,
})

sample({
  clock: selectTvChannel,
  filter: ({ free, purchaseInfo }) => Boolean(free) || Boolean(purchaseInfo), // free content or already paid
  fn: (selectTvChannel) => selectTvChannel?.categoryName,
  target: redirectUrlModel.redirectToTvPlayer,
})

sample({
  clock: selectTvChannel,
  source: external.$externalPaymentEnabled,
  filter: (external, channel) => external && isPaid(channel),
  fn: (_, channel) => formatChannelExternalPayment({ channel }),
  target: external.selectForExternalPayment,
})

sample({
  clock: selectTvChannelOnEpg,
  source: external.$externalPaymentEnabled,
  filter: (external, channel) => external && isPaid(channel),
  fn: (_, channel) =>
    formatChannelExternalPayment({ channel, shouldRefresh: true }),
  target: external.selectForExternalPayment,
})

sample({
  clock: [selectTvChannel, selectTvChannelOnEpg],
  source: external.$externalPaymentEnabled,
  filter: (externalPaymentEnabled, channel) =>
    !externalPaymentEnabled && isPaid(channel),
  fn: (_, channel) => channel,
  target: selectForInternalPayment,
})

sample({
  clock: selectForInternalPayment,
  filter: isPayForItem,
  fn: ({ priceSettings, ...rest }) => ({
    priceSettings: getPerItemPriceSettingsOnly(priceSettings),
    ...rest,
  }),
  target: payForTvChannel,
})

sample({
  clock: selectForInternalPayment,
  filter: isPayForBundle,
  fn: ({ priceSettings, ...rest }) => ({
    priceSettings: getPerSetPriceSettingsOnly(priceSettings),
    ...rest,
  }),
  target: payForTvBundle,
})

sample({
  clock: [payForTvBundle, payForTvChannel],
  fn: ({ priceSettings }) => priceSettings ?? null,
  target: priceSettingsModel.$priceSettings,
})

// TODO fix that should be in adapter as effect
sample({
  clock: payForTvBundle,
  fn: ({ priceSettings }) => ({
    bundleId: priceSettings?.[0].bundle?.id ?? 0,
  }),
  target: api.tvBundles.loadTvBundlePayInfo,
})

sample({
  clock: [payForTvChannel, payForTvBundle],
  target: anyPaymentInitiated,
})

sample({
  clock: api.tvBundles.loadTvBundlePayInfo.doneData,
  source: i18n.$t,
  fn: ({ t }, bundleInfo) => {
    const itemCountLabel = t('live_channel_number', {
      count: bundleInfo.contentQuantity,
    })

    return {
      name: bundleInfo.name,
      title: t('extras'),
      imageUrl: bundleInfo.landscapeImageUrl || '',
      labels: [itemCountLabel],
      priceSettings: bundleInfo.priceSettings,
      bundlePoster: true,
    }
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

sample({
  clock: api.tvBundles.loadTvBundlePayInfo.doneData,
  fn: (bundleInfo) => ({
    channelSetId: bundleInfo.id,
    channelIds: bundleInfo.channelIds,
  }),
  target: cartModel.$tvBundleInfoForCart,
})

sample({
  clock: payForTvChannel,
  source: i18n.$t,
  fn: ({ t }, channel) => ({
    name: channel.name,
    title: t('live_channel'),
    labels: null,
    priceSettings: channel.priceSettings ?? null,
    channelPosterUrl: channel.logoUrl,
  }),
  target: paymentInfoModel.$uiPaymentInfo,
})

sample({
  clock: payForTvChannel,
  fn: (channel) => ({
    channelId: channel.id,
  }),
  target: cartModel.$tvChannelInfoForCart,
})
